import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TouchableOpacity,
    ScrollView
} from 'react-native';
import moment from 'moment';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import { noticeColumns, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Modal from "react-native-modal";
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

const OpenModal = ({ modalVisible, setModalVisible, notice, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const date = moment(notice?.REGDATE).format('YYYY-MM-DD HH:mm');

    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={modalVisible}
            backdropOpacity={0.7}
            onBackdropPress={() => { setModalVisible(false); }}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.modalHeaderRow}>
                    <View style={{ flex: 1, marginRight: s(12) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }} numberOfLines={2}>
                            {notice?.TITLE}
                        </Text>
                    </View>
                    <TouchableOpacity
                        style={[styles.closeBtn, { backgroundColor: T.cardInner }]}
                        onPress={() => setModalVisible(false)}
                    >
                        <AntDesign name='close' size={s(16)} color={T.textSub} />
                    </TouchableOpacity>
                </View>

                {/* ─── 날짜 ─── */}
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: s(16), gap: s(6) }}>
                    <Ionicons name="time-outline" size={s(14)} color={T.muted} />
                    <Text style={{ fontSize: s(13), color: T.textSub }}>{date}</Text>
                </View>

                {/* ─── 구분선 ─── */}
                <View style={{ height: s(1), backgroundColor: T.border, marginBottom: s(16) }} />

                {/* ─── 내용 ─── */}
                <ScrollView
                    style={[styles.contentBox, { backgroundColor: T.cardInner, borderColor: T.border }]}
                    showsVerticalScrollIndicator={true}
                >
                    <Text style={{ fontSize: s(15), color: T.text, lineHeight: s(24) }}>
                        {notice?.CONTENT}
                    </Text>
                </ScrollView>

                {/* ─── 버튼 ─── */}
                <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: s(20) }}>
                    <TouchableOpacity
                        style={[styles.modalBtn, { backgroundColor: S.accent }]}
                        onPress={() => { setModalVisible(false); }}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>확인</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

export default function Notice({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [modalVisible, setModalVisible] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((list) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    const pageData = ((list) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [notice, setNotice] = useState<any>();
    const { dark, setDark } = useDarkMode();

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    useEffect(() => {
        if (!isFocused) return;
        Data.fetchData('notice/getNoticeList', {
        }, (result: any, count: any) => {
          if (count > 0) {
            setList(result);
          } else {
            setList([]);
          }
        }, true);
    }, [isFocused]);

    const onClick = (row: any) => {
        setNotice(row);
        setModalVisible(true);
    };

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>공지사항</Text>
                        {list?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={noticeColumns}
                    data={pageData}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(16), color: T.muted }}>
                                등록된 공지사항이 없습니다.
                            </Text>
                        </View>
                    }
                    onRowClicked={(row: any) => { onClick(row); }}
                />
                {(list || []).length > 0 && (
                    <TablePagination
                        rowCount={(list || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
            <OpenModal modalVisible={modalVisible} setModalVisible={setModalVisible} notice={notice} T={T} />
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalView: {
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(28),
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: s(12),
        elevation: 8,
        width: '50%',
        maxWidth: s(600),
    },
    modalHeaderRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: s(8),
    },
    closeBtn: {
        width: s(30),
        height: s(30),
        borderRadius: s(8),
        justifyContent: 'center',
        alignItems: 'center',
    },
    contentBox: {
        borderWidth: 1,
        borderRadius: s(12),
        padding: s(16),
        minHeight: s(200),
        maxHeight: s(350),
    },
    modalBtn: {
        paddingHorizontal: s(32),
        height: s(38),
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: s(10),
    },
});
